// src/pages/ChartPage.jsx
// S27H: Full-viewport advanced chart page — /chart/:ticker
// Unlocks: indicator sidebar · Bollinger · MACD · VWAP · EMA · Stochastic · Templates
// URL state: /chart/NVDA?tf=1M&indicators=bb,macd,vwap&template=daytrader
// D3 value: full-page dwell + indicator set = high-conviction ticker + strategy fingerprint

import { useMemo, useCallback } from 'react';
import PriceChart from '../components/PriceChart';

// ── Indicator keys that can appear in the URL ?indicators= param ────────────
const VALID_IND_KEYS = new Set(['bb', 'macd', 'vwap', 'ema1', 'ema2', 'stoch']);

export default function ChartPage({ ticker, isLoggedIn, token }) {

  // ── Parse URL query params on mount ────────────────────────────────────────
  // /chart/NVDA?tf=1M&indicators=bb,macd,vwap&template=daytrader
  // URL state takes precedence over MongoDB layout — share/bookmark must load exactly as linked
  const { initialTimeframe, initialIndicators, initialTemplate } = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const tf = p.get('tf') || '1M';
    const indicators = p.get('indicators')
      ? p.get('indicators').split(',').filter(k => VALID_IND_KEYS.has(k))
      : [];
    const template = p.get('template') || null;
    return { initialTimeframe: tf, initialIndicators: indicators, initialTemplate: template };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── URL state sync — called by PriceChart when indicator state changes ──────
  // Replaces URL (no back-stack entry) so state is always bookmark/share-able
  const handleIndicatorChange = useCallback(({ tf, indVisible, activeTemplate }) => {
    const indicators = Object.entries(indVisible)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const params = new URLSearchParams();
    params.set('tf', tf);
    if (indicators.length) params.set('indicators', indicators.join(','));
    if (activeTemplate)    params.set('template', activeTemplate);
    window.history.replaceState({}, '', '/chart/' + ticker + '?' + params.toString());
  }, [ticker]);

  // ── Back navigation — pop state so App.jsx handleRoute fires cleanly ────────
  const handleBack = useCallback(() => {
    window.history.pushState({}, '', '/stocks/' + ticker);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [ticker]);

  return (
    <div style={{ background: '#050d1a', minHeight: 'calc(100vh - 120px)' }}>

      {/* ── Top bar: back button + title ─────────────────────────────── */}
      <div style={{
        display:        'flex',
        alignItems:     'center',
        gap:            12,
        padding:        '10px 20px',
        borderBottom:   '1px solid rgba(255,255,255,0.06)',
        background:     'rgba(5,13,26,0.95)',
        position:       'sticky',
        top:            0,
        zIndex:         10,
      }}>
        <button
          onClick={handleBack}
          style={{
            display:      'flex',
            alignItems:   'center',
            gap:          6,
            padding:      '5px 12px',
            background:   'rgba(0,212,255,0.08)',
            border:       '1px solid rgba(0,212,255,0.2)',
            borderRadius: 6,
            color:        '#00d4ff',
            fontSize:     12,
            fontWeight:   600,
            cursor:       'pointer',
            fontFamily:   'inherit',
          }}
        >
          ← {ticker}
        </button>
        <span style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>
          <span style={{ background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900 }}>✦</span>
          {' '}MarketPulze Advanced Chart
        </span>
        <span style={{ fontSize: 11, color: '#4a4a6a', marginLeft: 4 }}>
          Indicators · Templates · Drawings
        </span>
      </div>

      {/* ── Full-viewport PriceChart ─────────────────────────────────── */}
      <PriceChart
        ticker={ticker}
        isLoggedIn={isLoggedIn}
        token={token}
        fullscreen
        initialTimeframe={initialTimeframe}
        initialIndicators={initialIndicators}
        initialTemplate={initialTemplate}
        onIndicatorChange={handleIndicatorChange}
      />

    </div>
  );
}
